import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { sentryEnabled } from "@/lib/sentry";
import { AlertTriangle, Beaker, Bug, CreditCard, Pill, Activity } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

/**
 * Operations Toolkit — admin-only utilities used to verify production
 * readiness without depending on real Stripe charges or pharmacy traffic.
 *
 * Surfaces:
 *   1. Stripe environment badge (test vs live)
 *   2. Sentry test-error trigger (verifies alert rules)
 *   3. Pharmacy webhook simulator (rehearse Rx loop end-to-end)
 *   4. Pharmacy connection status (last webhook + last error)
 */

type StripeMode = "test" | "live" | "unknown";

const StripeEnvBadge = () => {
  const [mode, setMode] = useState<StripeMode>("unknown");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-stripe-key");
        if (error) throw error;
        const key: string = data?.publishableKey || "";
        if (key.startsWith("pk_live_")) setMode("live");
        else if (key.startsWith("pk_test_")) setMode("test");
        else setMode("unknown");
      } catch {
        setMode("unknown");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const variant =
    mode === "live" ? "default" : mode === "test" ? "secondary" : "outline";
  const dot =
    mode === "live"
      ? "bg-green-500"
      : mode === "test"
        ? "bg-amber-500"
        : "bg-muted-foreground";

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <CreditCard size={16} className="text-primary" /> Stripe environment
        </CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${dot}`} />
          <span className="text-sm text-muted-foreground">
            Detected from publishable key
          </span>
        </div>
        <Badge variant={variant} className="uppercase tracking-wider text-[10px]">
          {loading ? "Checking…" : mode}
        </Badge>
      </CardContent>
    </Card>
  );
};

const SentryTestButton = () => {
  const { toast } = useToast();
  const [firing, setFiring] = useState(false);

  const trigger = () => {
    setFiring(true);
    try {
      throw new Error(
        `[Sentry test] manual error fired at ${new Date().toISOString()}`,
      );
    } catch (err) {
      // The ErrorBoundary above this won't catch async / event-handler throws,
      // so we deliberately re-throw via setTimeout to bubble to the global handler.
      setTimeout(() => {
        throw err;
      }, 0);
      toast({
        title: sentryEnabled ? "Test error fired" : "Test error fired (Sentry disabled in this env)",
        description: sentryEnabled
          ? "Check your Sentry project + PagerDuty / Slack within ~1 min."
          : "Sentry is disabled on localhost / preview. Test from production.",
      });
    } finally {
      setTimeout(() => setFiring(false), 500);
    }
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Bug size={16} className="text-primary" /> Sentry alert verification
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Throws one error to confirm your "new issue" alert rule pages on-call.
          Does not affect any data.
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            Sentry: <span className={sentryEnabled ? "text-green-600" : "text-amber-600"}>{sentryEnabled ? "active" : "disabled (dev/preview)"}</span>
          </span>
          <Button size="sm" variant="outline" onClick={trigger} disabled={firing}>
            <AlertTriangle size={14} className="mr-1.5" />
            {firing ? "Firing…" : "Trigger test error"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

interface RxOption {
  id: string;
  peptide_name: string;
  status: string;
  patient_id: string;
}

const PharmacySimulator = () => {
  const { toast } = useToast();
  const [rxList, setRxList] = useState<RxOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRx, setSelectedRx] = useState<string>("");
  const [event, setEvent] = useState<"filled" | "shipped">("filled");
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [secret, setSecret] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("prescriptions")
        .select("id, peptide_name, status, patient_id")
        .order("created_at", { ascending: false })
        .limit(50);
      setRxList(data || []);
      setLoading(false);
    })();
  }, []);

  const send = async () => {
    if (!selectedRx) {
      toast({ title: "Pick a prescription", variant: "destructive" });
      return;
    }
    if (!secret) {
      toast({ title: "Enter the pharmacy webhook secret", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pharmacy-webhook`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Webhook-Secret": secret,
        },
        body: JSON.stringify({
          prescriptionId: selectedRx,
          event,
          ...(event === "shipped" && tracking ? { trackingNumber: tracking } : {}),
          ...(event === "shipped" && carrier ? { carrier } : {}),
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || `HTTP ${res.status}`);
      }
      toast({
        title: `Pharmacy event "${event}" delivered`,
        description: `Rx now: ${payload?.status || "updated"}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Simulation failed", description: message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Beaker size={16} className="text-primary" /> Pharmacy webhook simulator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Posts a fake <code className="rounded bg-muted px-1 text-xs">filled</code> or{" "}
          <code className="rounded bg-muted px-1 text-xs">shipped</code> event so you can rehearse
          the patient-facing loop without a real pharmacy connection.
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Prescription</Label>
            <Select value={selectedRx} onValueChange={setSelectedRx} disabled={loading}>
              <SelectTrigger>
                <SelectValue placeholder={loading ? "Loading…" : "Select an Rx"} />
              </SelectTrigger>
              <SelectContent>
                {rxList.map((rx) => (
                  <SelectItem key={rx.id} value={rx.id}>
                    {rx.peptide_name} — {rx.status} — {rx.id.slice(0, 8)}
                  </SelectItem>
                ))}
                {!loading && rxList.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    No prescriptions yet
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Event</Label>
            <Select value={event} onValueChange={(v) => setEvent(v as "filled" | "shipped")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="filled">filled</SelectItem>
                <SelectItem value="shipped">shipped</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {event === "shipped" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Carrier (optional)</Label>
                <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tracking number (optional)</Label>
                <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="1Z…" />
              </div>
            </>
          )}

          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">Webhook secret</Label>
            <Input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Paste PHARMACY_WEBHOOK_SECRET to authenticate the request"
            />
            <p className="text-[11px] text-muted-foreground">
              The secret is only sent to your own pharmacy-webhook edge function and never stored.
            </p>
          </div>
        </div>

        <Button onClick={send} disabled={sending}>
          {sending ? "Sending…" : `Send "${event}" event`}
        </Button>
      </CardContent>
    </Card>
  );
};

interface PharmacyTraffic {
  lastEventAt: string | null;
  lastEvent: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  count24h: number;
}

const PharmacyStatusWidget = () => {
  const [data, setData] = useState<PharmacyTraffic | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const [lastEvent, count24, lastError] = await Promise.all([
        supabase
          .from("audit_logs")
          .select("action, created_at")
          .like("action", "pharmacy_%")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("audit_logs")
          .select("id", { count: "exact", head: true })
          .like("action", "pharmacy_%")
          .gte("created_at", since),
        supabase
          .from("admin_error_log")
          .select("error_message, created_at")
          .eq("resource_type", "pharmacy_webhook")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      setData({
        lastEventAt: lastEvent.data?.created_at ?? null,
        lastEvent: lastEvent.data?.action ?? null,
        lastErrorAt: lastError.data?.created_at ?? null,
        lastError: lastError.data?.error_message ?? null,
        count24h: count24.count ?? 0,
      });
      setLoading(false);
    })();
  }, []);

  const healthy = data && data.lastEventAt && !data.lastError;

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Pill size={16} className="text-primary" /> Pharmacy connection status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading || !data ? (
          <p className="text-sm text-muted-foreground">Checking…</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={healthy ? "default" : data.lastEvent ? "secondary" : "outline"} className="uppercase tracking-wider text-[10px]">
                {healthy ? "live" : data.lastEvent ? "events stale" : "no traffic yet"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Events (24h)</span>
              <span className="text-sm font-mono">{data.count24h}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last event</span>
              <span className="text-sm font-mono">
                {data.lastEventAt
                  ? `${data.lastEvent} · ${formatDistanceToNow(new Date(data.lastEventAt), { addSuffix: true })}`
                  : "never"}
              </span>
            </div>
            {data.lastError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs">
                <p className="font-medium text-destructive flex items-center gap-1.5">
                  <Activity size={12} /> Last webhook error{" "}
                  {data.lastErrorAt &&
                    `· ${formatDistanceToNow(new Date(data.lastErrorAt), { addSuffix: true })}`}
                </p>
                <p className="mt-1 font-mono text-muted-foreground break-all">{data.lastError}</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const OperationsToolkit = () => {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <StripeEnvBadge />
        <SentryTestButton />
      </div>
      <PharmacyStatusWidget />
      <PharmacySimulator />
    </div>
  );
};

export default OperationsToolkit;
